import React, { useState, useEffect, useRef } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import {
  format,
  isSameDay,
  endOfDay,
  subMonths,
  startOfMonth,
  isValid,
  parse,
  getMonth,
  getDate,
  getYear,
} from 'date-fns';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './DateRangePicker.css';
import * as Popover from '@radix-ui/react-popover';
import clsx from 'clsx';
import Button from '@dashboard/components/Button';
import { dateRanges } from '../../scenes/Dashboard/date-ranges';

const presetRanges = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 days', value: 'lastSevenDays' },
  { label: 'Last 4 weeks', value: 'lastFourWeeks' },
  { label: 'Last 3 months', value: 'lastThreeMonths' },
  { label: 'Last 12 months', value: 'lastTwelveMonths' },
  { label: 'Current billing period', value: 'currentBillingPeriod' },
  { label: 'Month to date', value: 'monthToDate' },
  { label: 'Quarter to date', value: 'quarterToDate' },
  { label: 'Year to date', value: 'yearToDate' },
  { label: 'All time', value: 'allTime' },
];

export default function DateRangePicker({
  value,
  onChange,
  startDate,
  endDate,
  onDateChange,
  className,
  hasSubscription = false,
  billingPeriodStart,
  billingPeriodEnd,
}) {
  // Create full preset ranges including conditional ones
  const [fullPresetRanges, setFullPresetRanges] = useState(presetRanges);

  // Update preset ranges when subscription status changes
  useEffect(() => {
    if (hasSubscription && billingPeriodStart && billingPeriodEnd) {
      setFullPresetRanges(presetRanges);
    } else {
      // Remove the current billing period option
      const newRanges = presetRanges.filter(range => range.value !== 'currentBillingPeriod');
      setFullPresetRanges(newRanges);
    }
  }, [hasSubscription, billingPeriodStart, billingPeriodEnd]);

  const [open, setOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState({ from: startDate, to: endDate });
  const [isSelectingCustomRange, setIsSelectingCustomRange] = useState(value === 'custom');
  const [pendingPreset, setPendingPreset] = useState(value !== 'custom' ? value : null);
  const [currentMonth, setCurrentMonth] = useState(subMonths(startOfMonth(new Date()), 1));

  // Start date fields
  const [startMonth, setStartMonth] = useState('');
  const [startDay, setStartDay] = useState('');
  const [startYear, setStartYear] = useState('');
  const [startInputError, setStartInputError] = useState('');

  // End date fields
  const [endMonth, setEndMonth] = useState('');
  const [endDay, setEndDay] = useState('');
  const [endYear, setEndYear] = useState('');
  const [endInputError, setEndInputError] = useState('');

  // Flag to track source of date changes
  const isCalendarSelection = useRef(false);
  // Flag to prevent multiple updates
  const isUpdatingFromManualInput = useRef(false);

  const today = endOfDay(new Date());

  // When the picker opens, use the current dates to set the initial state
  useEffect(() => {
    if (open) {
      // Set initial range
      if (value === 'custom' && startDate && endDate) {
        const initialRange = { from: startDate, to: endDate };
        setSelectedRange(initialRange);

        // Update input fields
        updateInputFieldsFromRange(initialRange);

        setIsSelectingCustomRange(true);
        setPendingPreset(null);

        // Always show the month containing the start date
        setCurrentMonth(startOfMonth(startDate));
      } else if (value === 'currentBillingPeriod' && billingPeriodStart && billingPeriodEnd) {
        // For current billing period, use the provided billing period dates
        const initialRange = { from: billingPeriodStart, to: billingPeriodEnd };
        setSelectedRange(initialRange);

        // Update input fields
        updateInputFieldsFromRange(initialRange);

        setIsSelectingCustomRange(false);
        setPendingPreset(value);

        // Always show the month containing the start date
        setCurrentMonth(startOfMonth(billingPeriodStart));
      } else if (value !== 'custom' && dateRanges[value]) {
        // For preset values, use the corresponding date range
        const presetStart = dateRanges[value].start;
        const presetEnd = dateRanges[value].end;

        const initialRange = { from: presetStart, to: presetEnd };
        setSelectedRange(initialRange);

        // Update input fields
        updateInputFieldsFromRange(initialRange);

        setIsSelectingCustomRange(false);
        setPendingPreset(value);

        // Always show the month containing the start date
        setCurrentMonth(startOfMonth(presetStart));
      } else {
        // Default to showing the current month
        setCurrentMonth(startOfMonth(new Date()));
      }
    }
  }, [open, value, startDate, endDate, billingPeriodStart, billingPeriodEnd]);

  // Update inputs when range changes from calendar selection
  useEffect(() => {
    if (!selectedRange?.from && !selectedRange?.to) return;

    // Only update the input fields if this change came from the calendar
    // and not from manual input
    if (isCalendarSelection.current && !isUpdatingFromManualInput.current) {
      if (selectedRange.from) {
        setStartMonth(String(getMonth(selectedRange.from) + 1));
        setStartDay(String(getDate(selectedRange.from)));
        setStartYear(String(getYear(selectedRange.from)));
        setStartInputError('');
      }

      if (selectedRange.to) {
        setEndMonth(String(getMonth(selectedRange.to) + 1));
        setEndDay(String(getDate(selectedRange.to)));
        setEndYear(String(getYear(selectedRange.to)));
        setEndInputError('');
      }
    }

    // Reset the calendar selection flag
    isCalendarSelection.current = false;
  }, [selectedRange]);

  const handlePresetClick = preset => {
    // Update pending preset
    setPendingPreset(preset.value);
    setIsSelectingCustomRange(false);

    if (preset.value === 'currentBillingPeriod' && billingPeriodStart && billingPeriodEnd) {
      // Use the provided billing period dates
      const newRange = {
        from: billingPeriodStart,
        to: billingPeriodEnd,
      };
      setSelectedRange(newRange);

      // Update input fields for the billing period
      updateInputFieldsFromRange(newRange);

      // Always show the month containing the start date
      setCurrentMonth(startOfMonth(billingPeriodStart));
    } else if (dateRanges[preset.value]) {
      // For other presets, get dates from dateRanges
      const presetStart = dateRanges[preset.value].start;
      const presetEnd = dateRanges[preset.value].end;

      // Update the selected range to show in the calendar
      const newRange = {
        from: presetStart,
        to: presetEnd,
      };
      setSelectedRange(newRange);

      // Update input fields for the preset
      updateInputFieldsFromRange(newRange);

      // Always show the month containing the start date
      if (presetStart) {
        setCurrentMonth(startOfMonth(presetStart));
      }
    }
  };

  const handleDateSelect = range => {
    isCalendarSelection.current = true;
    setSelectedRange(range);
    setIsSelectingCustomRange(true);
    setPendingPreset(null);

    // Update input fields when a date is selected on the calendar
    updateInputFieldsFromRange(range);

    // If user selects a new start date, ensure it's visible in the calendar
    if (
      range?.from &&
      (!selectedRange?.from || range.from.getTime() !== selectedRange.from.getTime())
    ) {
      setCurrentMonth(startOfMonth(range.from));
    }
  };

  // Helper function to update input fields from a date range
  const updateInputFieldsFromRange = range => {
    if (range?.from) {
      setStartMonth(String(getMonth(range.from) + 1));
      setStartDay(String(getDate(range.from)));
      setStartYear(String(getYear(range.from)));
      setStartInputError('');
    }

    if (range?.to) {
      setEndMonth(String(getMonth(range.to) + 1));
      setEndDay(String(getDate(range.to)));
      setEndYear(String(getYear(range.to)));
      setEndInputError('');
    }
  };

  // Validate a date from its parts
  const validateDateParts = (month, day, year) => {
    if (!month || !day || !year) {
      return false;
    }

    // Parse as strings to handle leading zeros
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    const yearNum = parseInt(year, 10);

    // Basic range checks
    if (
      monthNum < 1 ||
      monthNum > 12 ||
      dayNum < 1 ||
      dayNum > 31 ||
      yearNum < 1 ||
      yearNum > 9999
    ) {
      return false;
    }

    // Create date and validate
    const dateStr = `${month}/${day}/${year}`;
    const parsedDate = parse(dateStr, 'MM/dd/yyyy', new Date());

    if (!isValid(parsedDate)) {
      return false;
    }

    // Check if date is not in the future
    if (parsedDate > today) {
      return false;
    }

    return parsedDate;
  };

  // Update the start date when any part changes
  const updateStartDate = () => {
    const parsedDate = validateDateParts(startMonth, startDay, startYear);

    if (parsedDate) {
      setStartInputError('');

      // Check if start date is after end date
      if (selectedRange?.to && parsedDate > selectedRange.to) {
        setStartInputError('Start date cannot be after end date');
      } else {
        setStartInputError('');
        setSelectedRange(prev => ({ ...prev, from: parsedDate }));
        setIsSelectingCustomRange(true);
        setPendingPreset(null);

        // Always update the calendar view to show the month containing the start date
        setCurrentMonth(startOfMonth(parsedDate));
      }
    } else if (startMonth || startDay || startYear) {
      setStartInputError('Invalid date');
    }
  };

  // Helper function to advance to the next field when max digits are reached
  const advanceToNextField = (e, currentValue) => {
    // Only advance if this is the exact moment we've reached 2 digits
    // (i.e., the old value had 1 digit and the new value has 2)
    const newValue = e.target.value;

    if (newValue.length === 2 && currentValue.length === 1) {
      try {
        // Get the next input field based on the actual HTML structure
        const currentInputContainer = e.target.parentElement;
        const separator = currentInputContainer.nextElementSibling;

        if (separator && separator.nextElementSibling) {
          const nextInputContainer = separator.nextElementSibling;
          const nextInput = nextInputContainer.querySelector('input');

          if (nextInput) {
            setTimeout(() => {
              nextInput.focus();
              nextInput.select();
            }, 10);
          }
        }
      } catch (error) {
        console.log('Error navigating to next input:', error);
      }
    }
  };

  // Handle changes to the start date parts
  const handleStartMonthChange = e => {
    const oldValue = startMonth;
    let value = e.target.value;

    // Only allow digits and limit to max length
    if (!/^\d*$/.test(value)) return;
    if (value.length > 2) value = value.slice(0, 2);

    // Apply padding for values that can't be part of a two-digit sequence
    let displayValue = value;
    if (value.length === 1 && parseInt(value, 10) >= 2) {
      displayValue = value.padStart(2, '0');
    }

    // Always update the value
    setStartMonth(displayValue);

    // Auto-advance if we've just reached 2 digits from 1 digit
    if (value.length === 2 && oldValue.length === 1) {
      advanceToNextField(e, oldValue);
    }

    // Check if we can update the date picker (without validation messages)
    if (displayValue && startDay && startYear) {
      isUpdatingFromManualInput.current = true;
      const parsedDate = validateDateParts(displayValue, startDay, startYear);
      if (parsedDate) {
        setSelectedRange(prev => ({ ...prev, from: parsedDate }));
        setCurrentMonth(startOfMonth(parsedDate));
      }
      setTimeout(() => {
        isUpdatingFromManualInput.current = false;
      }, 0);
    }
  };

  const handleStartDayChange = e => {
    const oldValue = startDay;
    let value = e.target.value;

    // Only allow digits and limit to max length
    if (!/^\d*$/.test(value)) return;
    if (value.length > 2) value = value.slice(0, 2);

    // Apply padding for values that can't be part of a two-digit sequence
    let displayValue = value;
    if (value.length === 1 && parseInt(value, 10) >= 4) {
      displayValue = value.padStart(2, '0');
    }

    // Always update the value
    setStartDay(displayValue);

    // Auto-advance if we've just reached 2 digits from 1 digit
    if (value.length === 2 && oldValue.length === 1) {
      advanceToNextField(e, oldValue);
    }

    // Check if we can update the date picker (without validation messages)
    if (startMonth && displayValue && startYear) {
      isUpdatingFromManualInput.current = true;
      const parsedDate = validateDateParts(startMonth, displayValue, startYear);
      if (parsedDate) {
        setSelectedRange(prev => ({ ...prev, from: parsedDate }));
        setCurrentMonth(startOfMonth(parsedDate));
      }
      setTimeout(() => {
        isUpdatingFromManualInput.current = false;
      }, 0);
    }
  };

  const handleStartYearChange = e => {
    let value = e.target.value;

    // Only allow digits and limit to max length
    if (!/^\d*$/.test(value)) return;
    if (value.length > 4) value = value.slice(0, 4);

    // Always update the value
    setStartYear(value);

    // No auto-advance for year field

    // Check if we can update the date picker (without validation messages)
    if (startMonth && startDay && value && value.length === 4) {
      const parsedDate = validateDateParts(startMonth, startDay, value);
      if (parsedDate) {
        setSelectedRange(prev => ({ ...prev, from: parsedDate }));
        setCurrentMonth(startOfMonth(parsedDate));
      }
    }
  };

  // Update the end date when any part changes
  const updateEndDate = () => {
    const parsedDate = validateDateParts(endMonth, endDay, endYear);

    if (parsedDate) {
      setEndInputError('');

      // Check if end date is before start date
      if (selectedRange?.from && parsedDate < selectedRange.from) {
        setEndInputError('End date cannot be before start date');
      } else {
        setEndInputError('');
        setSelectedRange(prev => ({ ...prev, to: parsedDate }));
        setIsSelectingCustomRange(true);
        setPendingPreset(null);
      }
    } else if (endMonth || endDay || endYear) {
      setEndInputError('Invalid date');
    }
  };

  // Handle changes to the end date parts
  const handleEndMonthChange = e => {
    const oldValue = endMonth;
    let value = e.target.value;

    // Only allow digits and limit to max length
    if (!/^\d*$/.test(value)) return;
    if (value.length > 2) value = value.slice(0, 2);

    // Apply padding for values that can't be part of a two-digit sequence
    let displayValue = value;
    if (value.length === 1 && parseInt(value, 10) >= 2) {
      displayValue = value.padStart(2, '0');
    }

    // Always update the value
    setEndMonth(displayValue);

    // Auto-advance if we've just reached 2 digits from 1 digit
    if (value.length === 2 && oldValue.length === 1) {
      advanceToNextField(e, oldValue);
    }

    // Check if we can update the date picker (without validation messages)
    if (displayValue && endDay && endYear) {
      isUpdatingFromManualInput.current = true;
      const parsedDate = validateDateParts(displayValue, endDay, endYear);
      if (parsedDate) {
        setSelectedRange(prev => ({ ...prev, to: parsedDate }));
      }
      setTimeout(() => {
        isUpdatingFromManualInput.current = false;
      }, 0);
    }
  };

  const handleEndDayChange = e => {
    const oldValue = endDay;
    let value = e.target.value;

    // Only allow digits and limit to max length
    if (!/^\d*$/.test(value)) return;
    if (value.length > 2) value = value.slice(0, 2);

    // Apply padding for values that can't be part of a two-digit sequence
    let displayValue = value;
    if (value.length === 1 && parseInt(value, 10) >= 4) {
      displayValue = value.padStart(2, '0');
    }

    // Always update the value
    setEndDay(displayValue);

    // Auto-advance if we've just reached 2 digits from 1 digit
    if (value.length === 2 && oldValue.length === 1) {
      advanceToNextField(e, oldValue);
    }

    // Check if we can update the date picker (without validation messages)
    if (endMonth && displayValue && endYear) {
      isUpdatingFromManualInput.current = true;
      const parsedDate = validateDateParts(endMonth, displayValue, endYear);
      if (parsedDate) {
        setSelectedRange(prev => ({ ...prev, to: parsedDate }));
      }
      setTimeout(() => {
        isUpdatingFromManualInput.current = false;
      }, 0);
    }
  };

  const handleEndYearChange = e => {
    let value = e.target.value;

    // Only allow digits and limit to max length
    if (!/^\d*$/.test(value)) return;
    if (value.length > 4) value = value.slice(0, 4);

    // Always update the value
    setEndYear(value);

    // No auto-advance for year field

    // Check if we can update the date picker (without validation messages)
    if (endMonth && endDay && value && value.length === 4) {
      const parsedDate = validateDateParts(endMonth, endDay, value);
      if (parsedDate) {
        setSelectedRange(prev => ({ ...prev, to: parsedDate }));
      }
    }
  };

  // Handle keyboard up/down for incrementing/decrementing values
  const handleKeyDown = (e, type, value, setValue, min, max) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();

      // Parse the current value, defaulting to min-1 or max+1 if empty
      const numericValue =
        value === '' ? (e.key === 'ArrowUp' ? min - 1 : max + 1) : parseInt(value, 10);
      let newValue;

      if (e.key === 'ArrowUp') {
        newValue = numericValue >= max ? min : numericValue + 1;
      } else {
        newValue = numericValue <= min ? max : numericValue - 1;
      }

      // Apply the same conditional padding logic as for typing
      let stringValue = String(newValue);

      // For month inputs
      if (type === 'startMonth' || type === 'endMonth') {
        // Pad months >= 2 (that can't start a valid two-digit month)
        if (newValue >= 2 && newValue <= 9) {
          stringValue = stringValue.padStart(2, '0');
        }
      }
      // For day inputs
      else if (type === 'startDay' || type === 'endDay') {
        // Pad days >= 4 (that can't start a valid two-digit day)
        if (newValue >= 4 && newValue <= 9) {
          stringValue = stringValue.padStart(2, '0');
        }
      }

      // Always update the input value
      if (type === 'startMonth') {
        setStartMonth(stringValue);
      } else if (type === 'startDay') {
        setStartDay(stringValue);
      } else if (type === 'startYear') {
        setStartYear(stringValue);
      } else if (type === 'endMonth') {
        setEndMonth(stringValue);
      } else if (type === 'endDay') {
        setEndDay(stringValue);
      } else if (type === 'endYear') {
        setEndYear(stringValue);
      }

      // Update the date picker if we have valid values
      setTimeout(() => {
        isUpdatingFromManualInput.current = true;

        if (type.startsWith('start')) {
          const month = type === 'startMonth' ? stringValue : startMonth;
          const day = type === 'startDay' ? stringValue : startDay;
          const year = type === 'startYear' ? stringValue : startYear;

          if (month && day && year) {
            const parsedDate = validateDateParts(month, day, year);
            if (parsedDate) {
              setSelectedRange(prev => ({ ...prev, from: parsedDate }));
              setCurrentMonth(startOfMonth(parsedDate));
            }
          }
        } else {
          const month = type === 'endMonth' ? stringValue : endMonth;
          const day = type === 'endDay' ? stringValue : endDay;
          const year = type === 'endYear' ? stringValue : endYear;

          if (month && day && year) {
            const parsedDate = validateDateParts(month, day, year);
            if (parsedDate) {
              setSelectedRange(prev => ({ ...prev, to: parsedDate }));
            }
          }
        }

        setTimeout(() => {
          isUpdatingFromManualInput.current = false;
        }, 0);
      }, 0);
    }
  };

  const handleApply = () => {
    if (pendingPreset) {
      onChange(pendingPreset);
      setOpen(false);
    } else if (selectedRange?.from && selectedRange?.to) {
      onChange('custom');
      onDateChange(selectedRange.from, selectedRange.to);
      setOpen(false);
    }
  };

  const getDisplayText = () => {
    if (value === 'custom' && startDate && endDate) {
      if (isSameDay(startDate, endDate)) {
        return format(startDate, 'MMM d, yyyy');
      }
      return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
    }

    if (value === 'currentBillingPeriod' && billingPeriodStart && billingPeriodEnd) {
      return 'Current billing period';
    }

    const preset = fullPresetRanges.find(p => p.value === value);
    return preset?.label || 'Select date range';
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className={clsx(
            'relative flex items-center gap-1.5 py-1.5 px-4 pl-8 text-gray-800 bg-white rounded text-left border-none shadow-input focus:shadow-input-focus focus:outline-none appearance-none',
            className,
          )}
        >
          <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <span className="truncate">{getDisplayText()}</span>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="z-50 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden mt-1"
          sideOffset={4}
          alignOffset={0}
          align="end"
          collisionPadding="right"
          avoidCollisions={true}
        >
          <div className="flex">
            {/* Preset ranges */}
            <div className="w-48 border-r border-gray-200 bg-gray-50">
              <div className="p-2">
                {fullPresetRanges.map(preset => (
                  <button
                    key={preset.value}
                    onClick={() => handlePresetClick(preset)}
                    className={clsx(
                      'w-full text-left px-3 py-2 text-sm rounded transition-colors',
                      pendingPreset === preset.value && !isSelectingCustomRange
                        ? 'bg-chocolate-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100',
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Calendar and Footer */}
            <div className="flex flex-col">
              {/* Date Inputs */}
              <div className="flex p-4 gap-8">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500">Start</label>
                  <div className="relative border border-gray-200 rounded flex items-center bg-white py-0.5">
                    <input
                      type="text"
                      inputMode="numeric"
                      min="1"
                      max="12"
                      value={startMonth}
                      onChange={handleStartMonthChange}
                      onBlur={updateStartDate}
                      onKeyDown={e =>
                        handleKeyDown(e, 'startMonth', startMonth, setStartMonth, 1, 12)
                      }
                      placeholder="MM"
                      className="w-8 rounded text-sm text-center border-none p-0 mx-1 focus:outline-none focus:border-none focus:ring-0 focus:bg-gray-100"
                    />
                    <span className="text-gray-400">/</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      min="1"
                      max="31"
                      value={startDay}
                      onChange={handleStartDayChange}
                      onBlur={updateStartDate}
                      onKeyDown={e => handleKeyDown(e, 'startDay', startDay, setStartDay, 1, 31)}
                      placeholder="DD"
                      className="w-8 rounded text-sm text-center border-none p-0 mx-1 focus:outline-none focus:border-none focus:ring-0 focus:bg-gray-100"
                    />
                    <span className="text-gray-400">/</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      min="1"
                      max="9999"
                      value={startYear}
                      onChange={handleStartYearChange}
                      onBlur={updateStartDate}
                      onKeyDown={e =>
                        handleKeyDown(e, 'startYear', startYear, setStartYear, 1, 9999)
                      }
                      placeholder="YYYY"
                      className="w-12 rounded text-sm text-center border-none p-0 mx-1 focus:outline-none focus:border-none focus:ring-0 focus:bg-gray-100"
                    />
                    {startInputError && (
                      <div className="absolute left-0 -bottom-5 text-xs text-red-500">
                        {startInputError}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500">End</label>
                  <div className="relative border border-gray-200 rounded flex items-center bg-white py-0.5">
                    <input
                      type="text"
                      inputMode="numeric"
                      min="1"
                      max="12"
                      value={endMonth}
                      onChange={handleEndMonthChange}
                      onBlur={updateEndDate}
                      onKeyDown={e => handleKeyDown(e, 'endMonth', endMonth, setEndMonth, 1, 12)}
                      placeholder="MM"
                      className="w-8 rounded text-sm text-center border-none p-0 mx-1 focus:outline-none focus:border-none focus:ring-0 focus:bg-gray-100"
                    />
                    <span className="text-gray-400">/</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      min="1"
                      max="31"
                      value={endDay}
                      onChange={handleEndDayChange}
                      onBlur={updateEndDate}
                      onKeyDown={e => handleKeyDown(e, 'endDay', endDay, setEndDay, 1, 31)}
                      placeholder="DD"
                      className="w-8 rounded text-sm text-center border-none p-0 mx-1 focus:outline-none focus:border-none focus:ring-0 focus:bg-gray-100"
                    />
                    <span className="text-gray-400">/</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      min="1"
                      max="9999"
                      value={endYear}
                      onChange={handleEndYearChange}
                      onBlur={updateEndDate}
                      onKeyDown={e => handleKeyDown(e, 'endYear', endYear, setEndYear, 1, 9999)}
                      placeholder="YYYY"
                      className="w-12 rounded text-sm text-center border-none p-0 mx-1 focus:outline-none focus:border-none focus:ring-0 focus:bg-gray-100"
                    />
                    {endInputError && (
                      <div className="absolute left-0 -bottom-5 text-xs text-red-500">
                        {endInputError}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Calendar */}
              <div className="p-4">
                <DayPicker
                  mode="range"
                  selected={selectedRange}
                  onSelect={handleDateSelect}
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  numberOfMonths={2}
                  showOutsideDays={false}
                  className="rdp-custom"
                  modifiersClassNames={{
                    selected: 'rdp-day_selected',
                    today: 'rdp-day_today',
                  }}
                  disabled={{ after: today }}
                  toMonth={new Date()}
                  fromYear={2020}
                  toYear={new Date().getFullYear()}
                />
              </div>

              {/* Footer */}
              <div className="flex justify-end items-center px-4 pb-4 mt-auto">
                <div className="flex gap-2 ml-4">
                  <Button
                    onClick={() => {
                      setSelectedRange({});
                      setPendingPreset(null);
                      setIsSelectingCustomRange(false);
                      setStartMonth('');
                      setStartDay('');
                      setStartYear('');
                      setEndMonth('');
                      setEndDay('');
                      setEndYear('');
                      setStartInputError('');
                      setEndInputError('');
                      setOpen(false);
                    }}
                    appearance="secondary"
                    size="small"
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={handleApply}
                    disabled={
                      (!pendingPreset && (!selectedRange?.from || !selectedRange?.to)) ||
                      !!startInputError ||
                      !!endInputError
                    }
                    appearance="primary"
                    size="small"
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
